import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { EMPTY, Observable, shareReplay } from 'rxjs';
import { expand, map, reduce } from 'rxjs/operators';

/** Cadastros expostos pela API de regras — espelha o MetaRegra do backend. */
export type RecursoRegra = 'cte' | 'nfse' | 'natureza' | 'produto' | 'tomador';

/** Item de select devolvido pelos endpoints de lookup. */
export interface Opcao {
  value: string;
  label: string;
  /** So no lookup de filial: CNPJ da filial (M0_CGC), 14 digitos. */
  cnpj?: string;
}

export interface PageResult<T> {
  data: T[];
  hasNext: boolean;
}

/** Campos comuns a todos os cadastros de regra. */
export interface RegraBase {
  ativo: string;
  usrAlt?: string;
  /** Nome resolvido pela API — a tabela guarda só o código. */
  usrAltNome?: string;
  dtAlt?: string;
  hrAlt?: string;
}

interface ListResponse<T> {
  success: boolean;
  data: T[];
  hasNext?: boolean;
}

/**
 * Contrato que o po-combo espera no modo URL do p-filter-service: o
 * componente le `items` e usa `hasNext` para o scroll infinito.
 */
interface LookupResponse {
  items: Opcao[];
  hasNext?: boolean;
}

interface SaveResponse {
  success: boolean;
  message?: string;
}

/** Uma regra avaliada pelo motor, com o porquê de ter casado ou não. */
export interface PassoRastro {
  cod: string;
  ordem: string;
  vigIni: string;
  vigFim: string;
  casou: boolean;
  motivo: string;
}

/**
 * Resultado de uma das quatro decisões. `valor` muda de forma conforme a
 * decisão: lista de opções no TES do CT-e, número no TES da NFS-e, texto na
 * natureza e no produto, lógico no tomador.
 */
export interface Decisao {
  valor: unknown;
  regra: string;
  rastro: PassoRastro[];
}

/** Cenário que o motor consome — o mesmo nos três modos de entrada. */
export interface CenarioSimulacao {
  tipoDoc: 'cte' | 'nfse';
  filial: string;
  tipoProcesso: string;
  geraFinanceiro: boolean;
  simplesNacional: boolean;
  icmsIsento: boolean | null;
  issRetido: boolean;
  cnpjTomador: string;
  cnae: string;
  valorTotal: number;
  retencoes: { ir: number; pis: number; cofins: number; csll: number; inss: number };
}

export interface Simulacao {
  success: boolean;
  origem: string;
  tes: Decisao;
  natureza: Decisao;
  produto: Decisao;
  tomador?: Decisao;
}

/** Regra lida do cadastro para explicar a classificação de uma nota entregue. */
export interface RegraAplicada {
  cod: string;
  tabela: string;
  existe: boolean;
  ordem?: string;
  vigIni?: string;
  vigFim?: string;
  descricao?: string;
  ativo?: string;
}

export interface NotaSimulada {
  filial: string;
  doc: string;
  serie: string;
  fornecedor: string;
  loja: string;
  emissao: string;
  natureza: string;
  tes: string;
  produto: string;
  regraTes: string;
  regraNat: string;
  regraPrd: string;
}

/** Retorno do /regras/pdf/resolver. */
export interface ResolucaoPdf {
  filial: string;
  filialLabel: string;
  /** 'terceiros' | 'encomenda' | 'propria' | '' quando nao achou o processo. */
  tipoProcesso: string;
  processo: string;
  processoExiste: boolean;
  processoEncerrado: boolean;
}

export interface SimulacaoNota {
  success: boolean;
  nota: NotaSimulada;
  aplicada: { tes?: RegraAplicada; natureza?: RegraAplicada; produto?: RegraAplicada };
  temCenario: boolean;
  aviso?: string;
  cenario?: CenarioSimulacao;
  atual?: Simulacao;
}

/** Chave de uma nota já lançada, para o modo 2 do simulador. */
export interface ChaveNota {
  filial: string;
  doc: string;
  serie?: string;
  fornecedor?: string;
  loja?: string;
}

/**
 * Cliente único dos cinco cadastros de regras.
 *
 * Os endpoints têm o mesmo contrato (list/create/update, envelope
 * `{success,data,hasNext}`) e só mudam no último segmento da rota — por isso um
 * serviço parametrizado, e não cinco quase iguais que divergiriam na primeira
 * correção feita em só um deles.
 */
@Injectable({ providedIn: 'root' })
export class RegrasApiService {
  private readonly http = inject(HttpClient);
  private readonly base = 'portalnf/regras';

  // Filiais e naturezas não mudam durante a sessão e alimentam vários
  // formulários: sem cache, cada abertura de modal refaz as chamadas.
  private filiais$?: Observable<Opcao[]>;
  private naturezas$?: Observable<Opcao[]>;
  private codigosServico$?: Observable<string[]>;

  list<T>(
    recurso: RecursoRegra,
    page = 1,
    pageSize = 20,
    busca = '',
    ativo = '',
    extras: Record<string, string> = {}
  ): Observable<PageResult<T>> {
    let params = new HttpParams()
      .set('page', String(page))
      .set('pageSize', String(pageSize));

    const termo = (busca ?? '').trim();
    if (termo) { params = params.set('busca', termo); }
    if (ativo) { params = params.set('ativo', ativo); }

    Object.entries(extras).forEach(([chave, valor]) => {
      if (valor) { params = params.set(chave, valor); }
    });

    return this.http.get<ListResponse<T>>(`${this.base}/${recurso}`, { params }).pipe(
      map(r => ({ data: r.data ?? [], hasNext: !!r.hasNext }))
    );
  }

  create(recurso: RecursoRegra, body: Record<string, string>): Observable<void> {
    return this.http.post<SaveResponse>(`${this.base}/${recurso}`, body).pipe(map(() => void 0));
  }

  update(recurso: RecursoRegra, body: Record<string, string>): Observable<void> {
    return this.http.put<SaveResponse>(`${this.base}/${recurso}`, body).pipe(map(() => void 0));
  }

  filiais(): Observable<Opcao[]> {
    if (!this.filiais$) {
      this.filiais$ = this.http.get<LookupResponse>(`${this.base}/lookup/filial`).pipe(
        map(r => r.items ?? []),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.filiais$;
  }

  naturezas(busca = ''): Observable<Opcao[]> {
    // Com termo a consulta vai sempre ao servidor: a SED tem 1219 naturezas e
    // o endpoint devolve no maximo 300, entao cachear a primeira pagina
    // esconderia o resto.
    if (busca) {
      const params = new HttpParams().set('busca', busca);
      return this.http.get<LookupResponse>(`${this.base}/lookup/natureza`, { params })
        .pipe(map(r => r.items ?? []));
    }
    if (!this.naturezas$) {
      this.naturezas$ = this.http.get<LookupResponse>(`${this.base}/lookup/natureza`).pipe(
        map(r => r.items ?? []),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.naturezas$;
  }

  /**
   * Codigos de servico com de-para de produto ativo (ZOL tipo 1) - o que o
   * motor aceita como CNAE. O simulador casa o codigo lido do PDF com esta
   * lista pela mesma regra da inclusao manual (casarCodigoServico).
   *
   * A API limita a pagina a 200 linhas: percorre as paginas ate o fim.
   */
  codigosServico(): Observable<string[]> {
    if (!this.codigosServico$) {
      const tamanho = 200;
      const pagina = (n: number) =>
        this.list<{ chave: string }>('produto', n, tamanho, '', '1', { tipo: '1' })
          .pipe(map(r => ({ n, r })));

      this.codigosServico$ = pagina(1).pipe(
        expand(({ n, r }) => (r.hasNext ? pagina(n + 1) : EMPTY)),
        reduce((acc: string[], { r }) => acc.concat(r.data.map(x => (x.chave ?? '').trim())), []),
        map(lista => lista.filter(c => c)),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.codigosServico$;
  }

  /**
   * Simula um cenário informado. Atende tanto a nota hipotética quanto o PDF —
   * a extração do PDF acontece no browser e desemboca aqui, então os dois
   * percorrem o mesmo caminho de avaliação no backend.
   */
  simular(cenario: CenarioSimulacao): Observable<Simulacao> {
    return this.http.post<Simulacao>(`${this.base}/simular`, cenario);
  }

  /**
   * Completa o que o PDF nao entrega pronto: a filial pelo CNPJ do tomador e o
   * tipo do processo pela referencia externa. O PDF traz o CNPJ e a referencia;
   * o de-para para filial e tipo so existe na base.
   */
  resolverPdf(cnpj: string, processo: string): Observable<ResolucaoPdf> {
    let params = new HttpParams();
    if (cnpj) { params = params.set('cnpj', cnpj); }
    if (processo) { params = params.set('processo', processo); }

    return this.http.get<ResolucaoPdf>(`${this.base}/pdf/resolver`, { params });
  }

  /** Reconstitui a classificação de uma nota já lançada. */
  simularNota(chave: ChaveNota): Observable<SimulacaoNota> {
    let params = new HttpParams()
      .set('filial', chave.filial)
      .set('doc', chave.doc);

    if (chave.serie) { params = params.set('serie', chave.serie); }
    if (chave.fornecedor) { params = params.set('fornecedor', chave.fornecedor); }
    if (chave.loja) { params = params.set('loja', chave.loja); }

    return this.http.get<SimulacaoNota>(`${this.base}/simular/nota`, { params });
  }

}
